import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Box,
    LinearProgress,
    IconButton,
    Grid,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { HighlightOff } from '@mui/icons-material';
import { aiEnrichAttribute, AiConversationMessage } from '../../services/ai_service';
import EnhancedTextField from '../../../../components/EnhancedTextField';
import { HtmlPreview } from '../../../../components/EnhancedRte';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-rounded": {
        borderRadius: "12px",
        padding: "16px"
    },
    "& .MuiDialogTitle-root": {
        padding: 0
    },
    "& .MuiDialogContent-root": {
        padding: 0
    }
}));

interface AIEnrichmentDialogProps {
    open: boolean;
    onClose: () => void;
    attributeName: string;
    currentAttributes: Record<string, unknown>;
    onApply: (attributeName: string, value: string | string[], meta: { sessionId: string | null; regenerateCount: number; messages: AiConversationMessage[] }) => void;
    initialValue?: string | string[];
    attributeMetadata?: { Type?: string; Editor?: string; Options?: string[]; [key: string]: unknown };
}

function AIEnrichmentDialog(props: AIEnrichmentDialogProps) {
    const {
        open,
        onClose,
        attributeName,
        currentAttributes,
        onApply,
        initialValue = '',
        attributeMetadata = {}
    } = props;
    const theme = useTheme();

    const [enrichedValue, setEnrichedValue] = useState(initialValue);
    const [originalSuggestion, setOriginalSuggestion] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [regenerateCount, setRegenerateCount] = useState(0);
    const [error, setError] = useState(null);

    const getAttributeDisplayName = () => {
        const formatName = (str) => {
            if (str.includes('_')) {
                return str
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            }
            return str
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
        };

        return formatName(attributeName);
    };

    useEffect(() => {
        if (open && !isLoading) {
            generateEnrichment();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const generateEnrichment = async (withFeedback = false) => {
        setIsLoading(true);
        setError(null);

        try {
            const feedback = withFeedback && feedbackText.trim() ? feedbackText : null;

            const result = await aiEnrichAttribute(
                attributeName,
                currentAttributes,
                messages,
                sessionId,
                attributeMetadata,
                feedback
            );

            if (result.success) {
                setEnrichedValue(result.value);
                setOriginalSuggestion(result.originalSuggestion);
                setMessages(result.messages);
                setSessionId(result.sessionId);

                if (withFeedback) {
                    setRegenerateCount(prev => prev + 1);
                }
            } else {
                setError(result.error || 'Failed to generate content');
            }
        } catch (err) {
            console.error('AI enrichment error:', err);
            setError('An error occurred while generating content');
        } finally {
            setIsLoading(false);
            setFeedbackText('');
        }
    };

    const handleRegenerate = () => {
        generateEnrichment(true);
    };

    const handleAccept = () => {
        if (enrichedValue) {
            onApply(attributeName, enrichedValue, {
                sessionId,
                regenerateCount,
                messages
            });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth={true}
            disablePortal
        >
            <DialogTitle>
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <Typography style={{fontSize: "20px", fontWeight: 600}}>
                        Logicbroker Intelligence
                    </Typography>
                    <IconButton
                        style={{height: "20px", width: "20px"}}
                        onClick={handleCancel}
                        size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container style={{marginTop: "24px"}}>
                    {isLoading ? (
                        <Grid item container style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "16px",
                            margin: "10px 0",
                            width: "100%"
                        }}>
                            <LinearProgress style={{ width: '100%' }} />
                            <Typography variant="body2" color="textSecondary">
                                {regenerateCount > 0 ? 'Regenerating with your feedback...' : `Generating ${getAttributeDisplayName()}...`}
                            </Typography>
                        </Grid>
                    ) : error ? (
                        <Grid item container style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "10px 0",
                            width: "100%"
                        }}>
                            <Typography variant="body1" color="error">
                                {error}
                            </Typography>
                            <Button
                                variant="text"
                                onClick={() => generateEnrichment()}
                                style={{ marginTop: '16px' }}
                            >
                                Try Again
                            </Button>
                        </Grid>
                    ) : (
                        <>
                            <Grid item container style={{marginBottom: "24px", flexDirection: "column", width: "100%"}}>
                                <Typography style={{
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                    textAlign: "left"
                                }}>
                                    {getAttributeDisplayName()}
                                </Typography>
                                <Box style={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: "6px",
                                    padding: "12px",
                                    minHeight: "80px",
                                    maxHeight: "300px",
                                    backgroundColor: theme.palette.background.paper,
                                    textAlign: "left",
                                }}>
                                    {attributeMetadata.Type === 'select' || attributeMetadata.Type === 'list' ? (
                                        <Box>
                                            <Typography style={{
                                                fontSize: "1rem",
                                                lineHeight: 1.6,
                                                textAlign: "left"
                                            }}>
                                                {attributeMetadata.Type === 'select' ? 'Selected: ' : 'Suggested: '}
                                                <strong>{Array.isArray(enrichedValue) ? enrichedValue.join(', ') : (enrichedValue || 'No selection made yet')}</strong>
                                            </Typography>
                                            {originalSuggestion && originalSuggestion !== enrichedValue && (
                                                <Typography style={{
                                                    marginTop: '4px',
                                                    fontSize: '0.875rem',
                                                    color: theme.palette.text.secondary,
                                                    fontStyle: 'italic'
                                                }}>
                                                    AI suggested: "{originalSuggestion}" → Matched to: "{enrichedValue}"
                                                </Typography>
                                            )}
                                            {attributeMetadata.Options && (
                                                <Typography style={{
                                                    marginTop: '8px',
                                                    fontSize: '0.875rem',
                                                    color: theme.palette.text.secondary
                                                }}>
                                                    Available options: {attributeMetadata.Options.join(', ')}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : attributeMetadata.Editor === 'html' && enrichedValue ? (
                                        <Box style={{ maxHeight: "274px", overflowY: "auto" }}>
                                            <HtmlPreview value={String(enrichedValue)} />
                                        </Box>
                                    ) : (
                                        <Typography style={{
                                            fontSize: "1rem",
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                            textAlign: "left"
                                        }}>
                                            {enrichedValue || 'No content generated yet'}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item container style={{marginBottom: "24px", flexDirection: "column", width: "100%"}}>
                                <EnhancedTextField
                                    fullWidth
                                    multiline
                                    label="Feedback"
                                    placeholder="Provide feedback to improve the generated content..."
                                    value={feedbackText}
                                    onChange={(e) => setFeedbackText(e.target.value)}
                                    disabled={isLoading}
                                    style={{backgroundColor: "white", textAlign: "left"}}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item container style={{gap: "8px", width: "100%"}} justifyContent={"flex-end"}>
                        <Button
                            variant="text"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="text"
                            onClick={handleRegenerate}
                            disabled={isLoading || !feedbackText.trim()}
                        >
                            Regenerate
                        </Button>
                        <Button
                            data-testid="poc-ai-enrichment-accept"
                            color={"primary"}
                            variant={"text"}
                            onClick={handleAccept}
                            disabled={isLoading || !enrichedValue}
                        >
                            Accept
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </StyledDialog>
    );
}

export default AIEnrichmentDialog;
